import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Layers, Eye, FileCheck2, MessageSquare, PhoneCall, ShieldCheck,
  Landmark, Radar, Cpu, Building2, ScanSearch, Gavel, Scale,
  ClipboardCheck, SlidersHorizontal, BookOpen, Calculator, Server, FileDown,
  Compass, Rocket, Home as HomeIcon, TrendingUp,
} from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "../components/common/Section";
import ProblemSelector from "../components/home/ProblemSelector";
import CredibilityStrip from "../components/home/CredibilityStrip";
import FounderSpotlight from "../components/home/FounderSpotlight";
import BuyerSelector from "../components/home/BuyerSelector";
import ScenarioRunner from "../components/home/ScenarioRunner";
import TrustLayer from "../components/home/TrustLayer";
import BoardPack from "../components/home/BoardPack";
import { track, EVENTS } from "../lib/analytics";
import { Button } from "../components/ui/button";
import { useContent } from "../cms/ContentContext";
import heroTeam from "../assets/hero-team.png";

const HERO_IMG = heroTeam;

const SOLUTIONS = [
  { icon: Landmark, name: "Financial Services", desc: "Control AI and customer conversations at scale, with evidence.", to: "/financial-services" },
  { icon: Radar, name: "Defence & National Security", desc: "Independent governance around the AI you choose to deploy.", to: "/defence" },
  { icon: Cpu, name: "Enterprise AI", desc: "Runtime control for employee AI, copilots and agents.", to: "/enterprise-ai" },
  { icon: Building2, name: "Regulated Industries", desc: "Policy, oversight and evidence across regulated sectors.", to: "/regulated-industries" },
];

const PRODUCTS = [
  { icon: MessageSquare, name: "Vör", tag: "Governed chat widget", desc: "A customer-facing AI assistant that applies your policy in real time and captures evidence.", to: "/vor" },
  { icon: PhoneCall, name: "Lögberg", tag: "Conversation review", desc: "Screen regulated calls, prioritise material risks for human review and retain linked evidence.", to: "/logberg" },
  { icon: ShieldCheck, name: "Skjold", tag: "Document guard", desc: "Scan locally and block passports, IDs and confidential documents before they reach web AI tools.", to: "/skjold" },
];

const LIFECYCLE = [
  { icon: ScanSearch, t: "Identify", d: "Detect risk in prompts, responses and actions." },
  { icon: Gavel, t: "Decide", d: "Evaluate against your policy and risk appetite." },
  { icon: ShieldCheck, t: "Act", d: "Allow, warn, redact, block or escalate." },
  { icon: FileCheck2, t: "Evidence", d: "Record what happened and why." },
  { icon: Scale, t: "Review", d: "A human stays accountable for the outcome." },
];

function usePageMeta(title, description) {
  useEffect(() => {
    document.title = title;
    const m = document.querySelector('meta[name="description"]');
    if (m) m.setAttribute("content", description);
  }, [title, description]);
}

export default function Home() {
  const { content } = useContent();
  usePageMeta(
    "norse3 | See, control and evidence high-risk AI interactions",
    "norse3 is an independent, model-agnostic control layer for AI and human conversations. It applies your policies in real time, intervenes when required and creates a reviewable audit trail."
  );

  return (
    <div className="bg-card text-foreground">
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-primary/[0.06] via-background to-background">
        <div className="absolute -top-24 right-0 h-96 w-96 rounded-full bg-primary/15 blur-3xl" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-28 pb-16 lg:pt-36 lg:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="animate-fade-up">
              <Eyebrow>
                <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse-soft" />
                Independent AI governance & oversight
              </Eyebrow>
              <p className="mt-6 text-lg font-medium text-muted-foreground">Everyone is using AI. Someone still has to answer for it.</p>
              <h1 className="mt-3 text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.03] text-foreground text-balance">
                Control what AI can see, say and do.
              </h1>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-xl">
                norse3 is an independent control layer between your people, your AI and your
                workflows. It spots risk, applies your policies, brings a human in when it matters
                and keeps a clear record — so your team can adopt AI with confidence.
              </p>

              <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
                {[{ icon: Layers, label: "Model-agnostic" }, { icon: Eye, label: "Humans in control" }, { icon: FileCheck2, label: "Reviewable evidence" }].map((c) => {
                  const Icon = c.icon;
                  return (
                    <span key={c.label} className="inline-flex items-center gap-2 text-sm font-medium text-foreground/70 cursor-default select-none">
                      <Icon className="h-4 w-4 text-primary" /> {c.label}
                    </span>
                  );
                })}
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                  <Link to="/book-a-demo" onClick={() => track(EVENTS.HERO_DEMO_CLICK, { cta: "primary" })}>
                    Book a 20-minute walkthrough <ArrowRight className="ml-1.5 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline" className="border-primary/30 bg-card hover:bg-primary/10 text-primary">
                  <Link to="/#see-in-action" onClick={() => track(EVENTS.HERO_DEMO_CLICK, { cta: "secondary" })}>See norse3 in action</Link>
                </Button>
              </div>

              <FounderSpotlight />
            </div>

            {/* Human photo */}
            <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
              <div className="relative overflow-hidden rounded-xl border border-border shadow-xl">
                <img src={HERO_IMG} alt="The norse3 founding team" loading="eager"
                  className="h-[300px] sm:h-[420px] w-full object-cover object-[center_15%] lg:h-[500px]" data-testid="hero-team-photo" />
                <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-navy-950/60 to-transparent" aria-hidden="true" />
              </div>
              <div className="absolute inset-x-3 bottom-3 sm:inset-x-auto sm:-bottom-5 sm:left-6 rounded-lg border border-border bg-card/90 backdrop-blur px-4 py-3 sm:px-5 sm:py-4 shadow-lg" data-testid="hero-control-card">
                <div className="flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-md bg-primary/12 text-primary"><Eye className="h-5 w-5" /></span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">Humans stay in control</p>
                    <p className="text-xs text-muted-foreground">Escalation & sign-off stay with your people</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 right-4 rounded-xl border border-primary/25 bg-card px-4 py-2.5 shadow-md hidden sm:block">
                <p className="text-xs font-medium text-muted-foreground">Every interaction</p>
                <p className="text-sm font-semibold text-primary">→ reviewable evidence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CREDIBILITY */}
      <CredibilityStrip />

      {/* SOLUTIONS SHOWCASE */}
      <section className="border-t border-border py-16 lg:py-24 bg-card">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="Solutions" title="Find the right fit for your world." subtitle="One control layer, shaped for the environment you operate in." />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {SOLUTIONS.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.name} delay={i * 80}>
                  <Link to={s.to} data-testid={`home-solution-${i}`} className="card-rise group flex h-full flex-col rounded-lg border border-border bg-card p-6 shadow-sm hover:shadow-md hover:border-primary/40">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                    <h3 className="mt-5 text-lg font-bold text-foreground">{s.name}</h3>
                    <p className="mt-2 flex-1 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHAT ARE YOU TRYING TO CONTROL */}
      <ProblemSelector />

      {/* SEE IT IN ACTION */}
      <ScenarioRunner />

      {/* PRODUCTS */}
      <section className="border-t border-border py-16 lg:py-24 bg-muted/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="Products" title="Put norse3 to work where AI happens." subtitle="Purpose-built products, delivered where your conversations and staff actually use AI." />
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {PRODUCTS.map((p, i) => {
              const Icon = p.icon;
              return (
                <Reveal key={p.name} delay={i * 90}>
                  <Link to={p.to} data-testid={`home-product-${p.name.toLowerCase()}`} className="group flex h-full flex-col rounded-lg border border-border bg-card p-7 shadow-sm hover:shadow-md hover:border-primary/25 transition-all">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Icon className="h-6 w-6" /></span>
                    <h3 className="mt-5 text-xl font-bold text-foreground">{p.name}</h3>
                    <p className="mt-0.5 text-xs uppercase tracking-[0.14em] text-primary/80">{p.tag}</p>
                    <p className="mt-3 flex-1 text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                    <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary">Explore {p.name} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW NORSE3 THINKS */}
      <section className="border-t border-border py-16 lg:py-24 bg-card">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading align="center" eyebrow="How norse3 thinks" title="Identify, decide, act, evidence, review." subtitle="One consistent lifecycle applied to every high-risk interaction — inbound prompts and outbound responses alike." className="mx-auto" />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {LIFECYCLE.map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={i * 70}>
                  <div className="relative h-full rounded-lg border border-border bg-muted/40 p-6">
                    <span className="absolute right-4 top-4 font-mono text-xs text-primary/60">0{i + 1}</span>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{s.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* GOVERNANCE TOOLS */}
      <section className="border-t border-border py-16 lg:py-24 bg-muted/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="rounded-xl border border-border bg-card p-8 lg:p-12 shadow-sm">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <Reveal>
                <Eyebrow>Free · no sign-up</Eyebrow>
                <h2 className="mt-4 text-3xl sm:text-4xl font-bold text-foreground text-balance">Assess your AI governance in minutes.</h2>
                <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                  A suite of free, interactive self-assessment tools. Answer a few questions and get
                  an instant read on readiness, policy and deployment — with a downloadable evidence
                  pack you can share with your team.
                </p>
                <div className="mt-7 flex flex-col sm:flex-row gap-3">
                  <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                    <Link to="/governance-tools" onClick={() => track(EVENTS.TOOL_OPENED || "tool_opened", { from: "home_card" })} data-testid="home-governance-cta">
                      Start the free assessment <ArrowRight className="ml-1.5 h-4 w-4" />
                    </Link>
                  </Button>
                  <span className="inline-flex items-center gap-2 text-sm text-muted-foreground"><FileDown className="h-4 w-4 text-primary" /> Generates a downloadable evidence PDF</span>
                </div>
              </Reveal>
              <Reveal delay={120}>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { icon: ClipboardCheck, t: "Readiness Assessment" },
                    { icon: SlidersHorizontal, t: "Policy Control Builder" },
                    { icon: BookOpen, t: "Regulatory Framework Explorer" },
                    { icon: Calculator, t: "QA Coverage & Resource Calculator" },
                    { icon: TrendingUp, t: "ROI & Value Calculator" },
                    { icon: Server, t: "Deployment Configurator" },
                  ].map((tool) => {
                    const Icon = tool.icon;
                    return (
                      <Link key={tool.t} to="/governance-tools" data-testid={`home-tool-${tool.t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                        className="group flex items-center gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3.5 hover:border-primary/30 hover:bg-primary/[0.04] transition-colors">
                        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                        <span className="text-sm font-medium text-foreground">{tool.t}</span>
                      </Link>
                    );
                  })}
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* BUYER JOURNEY */}
      <BuyerSelector />

      {/* ENTERPRISE TRUST + TEAM */}
      <TrustLayer />

      {/* BOARD PACK */}
      <BoardPack />

      {/* CASE STUDIES TEASER */}
      <section className="border-t border-border py-16 lg:py-24 bg-muted/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="In practice" title="See what controlled AI adoption looks like." subtitle="Illustrative scenarios — from a mortgage lender to a defence supplier — showing risk spotted, policy applied, a human kept accountable and evidence produced." />
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { icon: HomeIcon, sector: "Mortgage lender", t: "Catching potential vulnerability before it becomes a poor outcome." },
              { icon: TrendingUp, sector: "Wealth manager", t: "Letting staff use AI without leaking client information." },
              { icon: Cpu, sector: "Defence supplier", t: "Keeping a human in the loop as AI starts to take actions." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <Reveal key={c.sector} delay={i * 80}>
                  <Link to="/case-studies" data-testid={`home-casestudy-${i}`} className="group flex h-full flex-col rounded-lg border border-border bg-card p-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-all">
                    <div className="flex items-center gap-2.5">
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{c.sector}</span>
                    </div>
                    <p className="mt-4 flex-1 text-base font-semibold text-foreground leading-snug">{c.t}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">Read the scenario <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" /></span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW ENGAGEMENT WORKS */}
      <section className="border-t border-border py-16 lg:py-24 bg-card">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading align="center" className="mx-auto" eyebrow="How engagement works" title="A clear path from first look to live." subtitle="A low-risk way in — see it working before you commit, and deploy to fit your environment." />
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { icon: Compass, t: "Discovery", d: "A short walkthrough against a scenario from your world — no obligation." },
              { icon: Rocket, t: "Pilot", d: "A scoped pilot on real workflows so you see the evidence it produces." },
              { icon: ShieldCheck, t: "Deploy", d: "Hosted, private cloud or your own environment, configured to your policy." },
              { icon: FileCheck2, t: "Assure", d: "Ongoing oversight, review and management information for your team." },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.t} delay={i * 70}>
                  <div className="relative h-full rounded-lg border border-border bg-muted/40 p-6">
                    <span className="absolute right-4 top-4 font-mono text-xs text-primary/60">0{i + 1}</span>
                    <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-primary/12 text-primary"><Icon className="h-5 w-5" /></span>
                    <h3 className="mt-4 text-base font-semibold text-foreground">{s.t}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.d}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border py-20 lg:py-28 bg-gradient-to-b from-background to-primary/[0.05]">
        <div className="mx-auto max-w-4xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground text-balance">Understand where norse3 sits in your estate.</h2>
            <p className="mt-4 text-lg text-muted-foreground">Book a friendly 20-minute walkthrough and see how norse3 identifies, decides, acts, evidences and supports review across your AI and human conversations.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary/30 bg-card hover:bg-primary/10 text-primary">
                <Link to="/platform">Explore the platform</Link>
              </Button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
